import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Loader, Button } from 'cdk-radial';
import {
  getSystemInfoForApis,
  getDealerApiPreferences,
  resetDealerApiPreferencesUpdateStatus,
  setApiSubscriptionSettings,
  setSelectedApiSubscriptionSettings,
  updateDealerApiPreferences,
} from '../../../../redux/connectedComponent/dealerApiPreferences/dealerApiPreferences.slice';
import './EditApiSubscriptionSettings.scss';
import SolutionHeader from '../../SolutionHeader/SolutionHeader';
import { setScreen } from '../../../../redux/connectedComponent/uiFactory/uiFactory.slice';
import { screens } from '../../../config/uiFactory.config';
import {
  SubscriptionSettingsApiCard,
  SubscriptionSettingsLocationsCard,
  SubscriptionSettingsSelectionCard,
} from '../../../stepper/stepperBody/stepperBodyComponents/SelectApiSubscriptionSettings';
import Notification from '../../../notification/Notification';
import { NOTIFICATION_TYPES } from '../../../../constants/constants';
import Page from '../../../page';
import styled from 'styled-components';
import { deepEqual } from '../../../../utils/utils';
import { trackPageView } from '../../../../utils/gtm';

const ErrorWrapper = styled.div`
  text-align: center;
`;

export const EditApiSubscriptionSettings = ({
  solutionData,
  additionalData: { subscriptionData },
}) => {
  const {
    id: solutionId,
    registration_details: { api_details: apis },
  } = solutionData;
  const dispatch = useDispatch();
  const state = useSelector((state) => state);

  const { authenticationToken: token, allLocations } = state.loginDetailsSlice;

  const {
    fetchingDealerApiPreferences,
    selectedDealerApiPreferences,
    failedToFetchDealerApiPreferences,
    fetchingSystemInfo,
    dealerApiPreferences,
    failedToFetchSystemInfo,
    preferences,
    updatingDealerApiPreferences,
    updatedDealerApiPreferences,
    failedToUpdateDealerApiPreferences,
  } = state.dealerApiPreferencesSlice;

  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedApi, setSelectedApi] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationContent, setNotificationContent] = useState('');
  const [notificationType, setNotificationType] = useState('');
  const [selectedApiPreferences, setSelectedApiPreferences] = useState({});

  const isLoading =
    fetchingDealerApiPreferences ||
    fetchingSystemInfo ||
    updatingDealerApiPreferences;

  const loaderLabel =
    fetchingDealerApiPreferences || fetchingSystemInfo
      ? 'Fetching API Subscription Settings...'
      : 'Updating API Subscription Settings...';

  const hasError = failedToFetchDealerApiPreferences || failedToFetchSystemInfo;

  const mappedLocationsData = Array.isArray(subscriptionData)
    ? subscriptionData.map((sub) => {
        const locationDetails = allLocations.find(
          (loc) => loc.entityId === sub.entityId
        );

        const fullAddress =
          Array.isArray(locationDetails.addresses) &&
          locationDetails.addresses[0] &&
          locationDetails.addresses[0]?.streetAddress +
            ', ' +
            locationDetails.addresses[0]?.city;

        return {
          address: fullAddress,
          cmfNumber: locationDetails.cmfNumber,
          entityId: locationDetails.entityId,
          location: locationDetails.partyName,
          storeId: locationDetails.storeId,
        };
      })
    : [];

  const apisThatSupportDealerApiPreferences = useMemo(() => {
    const apiDetails = apis || [];

    const supportedApis = Object.entries(dealerApiPreferences || {})
      .map(([k, v]) => (v ? apiDetails.find((api) => api.id === k) : null))
      .filter((v) => v);

    return supportedApis;
  }, [dealerApiPreferences, apis]);

  // Filter APIs based on the selected location's active subscriptions
  const availableApisForSelectedLocation = useMemo(() => {
    if (!selectedLocation || !selectedDealerApiPreferences) {
      return apisThatSupportDealerApiPreferences;
    }

    const locationEntityId = selectedLocation.entityId;
    const subscribedApiIds = selectedDealerApiPreferences[locationEntityId]
      ? Object.keys(selectedDealerApiPreferences[locationEntityId])
      : [];

    return apisThatSupportDealerApiPreferences.filter((api) =>
      subscribedApiIds.includes(api.id),
    );
  }, [
    selectedLocation,
    selectedDealerApiPreferences,
    apisThatSupportDealerApiPreferences,
  ]);

  // Check if there are any changes from original preferences
  const hasChanges = useMemo(() => {
    return !deepEqual(preferences, selectedApiPreferences);
  }, [preferences, selectedApiPreferences]);

  // Validate that all required API options are configured for each location
  const canProceed = useMemo(() => {
    if (
      !mappedLocationsData?.length ||
      !apisThatSupportDealerApiPreferences?.length ||
      !preferences
    ) {
      return false;
    }

    for (const location of mappedLocationsData) {
      const subscribedApiIds = selectedDealerApiPreferences[location.entityId]
        ? Object.keys(selectedDealerApiPreferences[location.entityId])
        : [];

      for (const api of apisThatSupportDealerApiPreferences) {
        if (!subscribedApiIds.includes(api.id)) {
          continue;
        }

        const apiOptions = dealerApiPreferences[api.id] || [];
        for (const option of apiOptions) {
          if (option.required) {
            const locationPrefs = preferences[location.entityId];
            const apiPrefs = locationPrefs?.[api.id];

            // Check if preferences exist for this location and API
            if (!locationPrefs || !apiPrefs) {
              return false;
            }

            // Check if the specific required option has a valid value
            const optionValue = apiPrefs[option.name];

            if (optionValue === undefined || optionValue === null) {
              return false;
            }

            if (Array.isArray(optionValue) && optionValue.length === 0) {
              return false;
            }

            if (typeof optionValue === 'string' && optionValue.trim() === '') {
              return false;
            }
          }
        }
      }
    }

    return true;
  }, [
    mappedLocationsData,
    preferences,
    apisThatSupportDealerApiPreferences,
    dealerApiPreferences,
    selectedDealerApiPreferences,
  ]);

  const onBackButtonClick = () =>
    dispatch(setScreen({ screens: screens.SUBSCRIPTION_MANAGEMENT }));

  const handleSaveButton = () => {
    const updatedPreferences = {};

    for (const entityId in preferences) {
      for (const apiId in preferences[entityId]) {
        const currentSettings = preferences[entityId][apiId];
        const originalSettings = selectedApiPreferences[entityId]?.[apiId];

        // Compare current settings with original to detect changes
        if (!deepEqual(currentSettings, originalSettings)) {
          if (!updatedPreferences[entityId]) {
            updatedPreferences[entityId] = {};
          }
          updatedPreferences[entityId][apiId] = {
            dealerApiPreferences: currentSettings,
          };
        }
      }
    }

    // Only dispatch if there are actual changes
    if (Object.keys(updatedPreferences).length > 0) {
      dispatch(
        updateDealerApiPreferences({
          appId: solutionId,
          dealerApiPreferences: updatedPreferences,
          token,
        })
      );
    }
  };

  const handleDismissNotification = () => {
    setShowNotification(false);
    dispatch(resetDealerApiPreferencesUpdateStatus());
  };

  useEffect(() => {
    const appName = solutionData?.overview?.name;

    if (updatedDealerApiPreferences) {
      setNotificationContent(
        `You updated API Subscription Settings for app ${appName} successfully`
      );
      setNotificationType(NOTIFICATION_TYPES.POSITIVE);
      setShowNotification(true);
      setSelectedApiPreferences({ ...preferences });
    } else if (failedToUpdateDealerApiPreferences) {
      setNotificationContent(
        `Failed to update API Subscription Settings for app ${appName}, please try again`
      );
      setNotificationType(NOTIFICATION_TYPES.NEGATIVE);
      setShowNotification(true);
    } else {
      setShowNotification(false);
    }
  }, [
    updatedDealerApiPreferences,
    failedToUpdateDealerApiPreferences,
    solutionData,
  ]);

  useEffect(() => {
    trackPageView('Manage API Subscription Settings');
    const mappedApiList =
      Array.isArray(apis) &&
      apis.map((api) => ({ id: api?.id, apiType: api?.apiType }));

    const entityIds =
      Array.isArray(subscriptionData) &&
      subscriptionData.map((sub) => sub.entityId);

    dispatch(
      getSystemInfoForApis({ apis: mappedApiList, appId: solutionId, token })
    );
    dispatch(
      getDealerApiPreferences({
        solutionId,
        entityIds,
        token,
      })
    );
  }, []);

  useEffect(() => {
    // Set default selected location and API
    if (
      !selectedLocation &&
      Array.isArray(mappedLocationsData) &&
      mappedLocationsData.length > 0
    ) {
      setSelectedLocation(mappedLocationsData[0]);
    }

    if (
      !selectedApi &&
      Array.isArray(availableApisForSelectedLocation) &&
      availableApisForSelectedLocation.length > 0
    ) {
      setSelectedApi({
        apiId: availableApisForSelectedLocation[0].id,
        selectedLocation: mappedLocationsData[0],
      });
    }
  }, [
    mappedLocationsData,
    availableApisForSelectedLocation,
    selectedLocation,
    selectedApi,
  ]);

  // Reset selected API when location changes if the currently selected API is not available for the new location
  useEffect(() => {
    if (selectedLocation && selectedApi && availableApisForSelectedLocation) {
      const isCurrentApiAvailable = availableApisForSelectedLocation.some(
        (api) => api.id === selectedApi.apiId,
      );

      if (
        !isCurrentApiAvailable &&
        availableApisForSelectedLocation.length > 0
      ) {
        setSelectedApi({
          apiId: availableApisForSelectedLocation[0].id,
          selectedLocation: selectedLocation,
        });
      }
    }
  }, [selectedLocation, availableApisForSelectedLocation, selectedApi]);

  useEffect(() => {
    const selectedApiPreferences = {};

    for (const entityId in selectedDealerApiPreferences) {
      for (const apiId in selectedDealerApiPreferences[entityId]) {
        if (!selectedApiPreferences[entityId]) {
          selectedApiPreferences[entityId] = {};
        }
        selectedApiPreferences[entityId][apiId] =
          selectedDealerApiPreferences[entityId][apiId].dealerApiPreferences;
      }
    }
    setSelectedApiPreferences(selectedApiPreferences);
    dispatch(setSelectedApiSubscriptionSettings(selectedApiPreferences));
  }, [selectedDealerApiPreferences]);

  return (
    <Page>
      <SolutionHeader
        solutionData={solutionData}
        onBackButtonClickHandler={onBackButtonClick}
        appName={solutionData?.overview?.name}
        publisherName={solutionData?.orgName}
      />
      {hasError ? (
        <ErrorWrapper>
          <h1>Something went wrong.</h1>
          <h2>Please contact Fortellis Support at support@fortellis.io</h2>
        </ErrorWrapper>
      ) : (
        <>
          {isLoading ? (
            <div className="edit-api-subscription-settings-loader">
              <Loader label={loaderLabel} />
            </div>
          ) : !Object.keys(dealerApiPreferences).length ? (
            <div className="no-api-preferences-message">
              This app does not have any APIs that support API Subscription
              Settings.
            </div>
          ) : (
            <>
              <div className="edit-api-subscription-settings-container">
                <SubscriptionSettingsLocationsCard
                  locations={mappedLocationsData}
                  selectedLocation={selectedLocation}
                  selectLocation={(loc) => setSelectedLocation(loc)}
                />
                <SubscriptionSettingsApiCard
                  locations={mappedLocationsData}
                  selectedLocation={selectedLocation}
                  apiDetails={availableApisForSelectedLocation}
                  selectApi={(api) => setSelectedApi(api)}
                  selectedApi={selectedApi}
                />

                <SubscriptionSettingsSelectionCard
                  apiOptions={dealerApiPreferences[selectedApi?.apiId] || []}
                  locations={mappedLocationsData}
                  selectedApi={selectedApi}
                  selectedLocation={selectedLocation}
                  apiDetails={availableApisForSelectedLocation}
                  setApiSubscriptionSettings={(settings) => {
                    dispatch(
                      setApiSubscriptionSettings({
                        orgId: selectedLocation.entityId,
                        apiId: selectedApi.apiId,
                        settings: settings,
                      })
                    );
                  }}
                  selectedDealerApiPreferences={preferences}
                />
              </div>
              <div className="edit-api-subscription-settings-save-button">
                <Button
                  backgroundAppearance="light"
                  iconColor="primary"
                  iconSize={24}
                  isDisabled={!canProceed || !hasChanges}
                  onClick={handleSaveButton}
                  size="standard"
                  text="Save"
                />
              </div>
            </>
          )}
        </>
      )}

      {showNotification && (
        <Notification
          content={notificationContent}
          variant={notificationType}
          actions={[{ text: 'Dismiss' }]}
          onDismiss={handleDismissNotification}
          customStyle={{ marginTop: '70px' }}
        />
      )}
    </Page>
  );
};
